import type { ArticleMeta } from "../types/article";

const FRONTMATTER_PATTERN = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;

function stripQuotes(value: string): string {
	if (value.length >= 2) {
		const first = value[0];
		const last = value[value.length - 1];
		if ((first === '"' || first === "'") && first === last) {
			return value.slice(1, -1);
		}
	}
	return value;
}

function readFields(block: string, source: string): Record<string, string> {
	const fields: Record<string, string> = {};
	for (const line of block.split("\n")) {
		const trimmed = line.trim();
		if (trimmed.length === 0) continue;
		// Split on the first colon only so values containing ":" (e.g. URLs) survive.
		const separator = trimmed.indexOf(":");
		if (separator === -1) {
			throw new Error(
				`Invalid frontmatter line in ${source} (expected "key: value"): "${trimmed}"`
			);
		}
		const key = trimmed.slice(0, separator).trim();
		fields[key] = stripQuotes(trimmed.slice(separator + 1).trim());
	}
	return fields;
}

/**
 * Parses an article's leading `--- ... ---` frontmatter block plus markdown body.
 * Content is trusted (authored in-repo) and the schema is a fixed flat set of
 * scalars, so we parse it directly instead of pulling in a YAML dependency.
 * Throws on malformed input so a broken article surfaces at dev/build time
 * rather than rendering blank in production.
 */
export function parseFrontmatter(
	raw: string,
	source: string
): { meta: ArticleMeta; body: string } {
	const match = raw.match(FRONTMATTER_PATTERN);
	if (!match) {
		throw new Error(`Missing or malformed frontmatter in ${source}`);
	}

	const fields = readFields(match[1], source);
	const { title, category, order, loom } = fields;

	if (!title) throw new Error(`Missing "title" in ${source}`);
	if (!category) throw new Error(`Missing "category" in ${source}`);
	if (!order) throw new Error(`Missing "order" in ${source}`);

	const parsedOrder = Number(order);
	if (Number.isNaN(parsedOrder)) {
		throw new Error(`"order" must be a number in ${source} (got "${order}")`);
	}

	return {
		meta: { title, category, order: parsedOrder, ...(loom ? { loom } : {}) },
		body: match[2].trim(),
	};
}
